import { validateStructure, type UnitNature } from './structure';

export const STUDY_PLAN_ISSUE_CODES = [
  'no_curriculum',
  'credits',
  'empty_semester',
  'empty_unit',
  'no_class',
  'weight',
  'hours',
  'dates',
  'dates_order',
  'dates_outside',
] as const;
export type StudyPlanIssueCode = (typeof STUDY_PLAN_ISSUE_CODES)[number];

export interface StudyPlanCourse {
  id: string;
  name: string;
  credits: number;
  coefficient: number;
  hoursCm: number;
  hoursTd: number;
  hoursTp: number;
}

export interface StudyPlanUnit {
  id: string;
  code: string;
  nature: UnitNature;
  courses: readonly StudyPlanCourse[];
}

export interface StudyPlanSemester {
  id: string;
  index: number;
  targetCredits: number;
  units: readonly StudyPlanUnit[];
}

export interface StudyPlanLevel {
  id: string;
  code: string;
  groupCount: number;
}

export interface CoursePeriodInput {
  courseId: string;
  startsOn: string;
  endsOn: string;
}

export interface StudyPlanIssue {
  code: StudyPlanIssueCode;
  semesterIndex: number | null;
  levelCode: string | null;
  unitCode: string | null;
  courseId: string | null;
  courseName: string | null;
  credits: number | null;
  target: number | null;
}

const issue = (code: StudyPlanIssueCode, extra: Partial<StudyPlanIssue> = {}): StudyPlanIssue => ({
  code,
  semesterIndex: null,
  levelCode: null,
  unitCode: null,
  courseId: null,
  courseName: null,
  credits: null,
  target: null,
  ...extra,
});

/**
 * A study plan can be validated when the parcours totals its credits, every level has a class,
 * every basket has subjects, and every subject has a weight, a hourly volume and a teaching period
 * inside the academic year.
 */
export function validateStudyPlan(input: {
  hasCurriculum: boolean;
  semesters: readonly StudyPlanSemester[];
  levels: readonly StudyPlanLevel[];
  periods: readonly CoursePeriodInput[];
  year: { startsOn: string; endsOn: string };
}): { ok: boolean; issues: StudyPlanIssue[] } {
  const issues: StudyPlanIssue[] = [];
  if (!input.hasCurriculum) issues.push(issue('no_curriculum'));

  for (const s of validateStructure(
    input.semesters.map((s) => ({
      id: s.id,
      index: s.index,
      targetCredits: s.targetCredits,
      units: s.units.map((u) => ({ nature: u.nature, courses: u.courses })),
    })),
  ).issues) {
    issues.push(issue('credits', { semesterIndex: s.index, credits: s.credits, target: s.target }));
  }

  for (const semester of input.semesters) {
    if (semester.units.length === 0) {
      issues.push(issue('empty_semester', { semesterIndex: semester.index }));
      continue;
    }
    for (const unit of semester.units) {
      if (unit.courses.length === 0) {
        issues.push(issue('empty_unit', { semesterIndex: semester.index, unitCode: unit.code }));
        continue;
      }
      for (const course of unit.courses) {
        const where = {
          semesterIndex: semester.index,
          unitCode: unit.code,
          courseId: course.id,
          courseName: course.name,
        };
        if (course.coefficient <= 0) issues.push(issue('weight', where));
        if (course.hoursCm + course.hoursTd + course.hoursTp <= 0) issues.push(issue('hours', where));
        const period = input.periods.find((p) => p.courseId === course.id);
        if (!period || !period.startsOn || !period.endsOn) {
          issues.push(issue('dates', where));
          continue;
        }
        if (period.startsOn >= period.endsOn) issues.push(issue('dates_order', where));
        if (period.startsOn < input.year.startsOn || period.endsOn > input.year.endsOn)
          issues.push(issue('dates_outside', where));
      }
    }
  }

  for (const level of input.levels) {
    if (level.groupCount < 1) issues.push(issue('no_class', { levelCode: level.code }));
  }

  return { ok: issues.length === 0, issues };
}
